import type {NoteDefinition, RequiredItem} from '../NoteForm';
import {isFutureDate} from '../NoteForm';
import {
  FOLLOW_UP_WITH_CLIENT,
  NOT_REACHED,
  OTHER,
  OTHER_PLACE_OF_SERVICE,
} from './choices';
import {
  composeNarrative,
  durationMinutes,
  isEndInFuture,
  narrativeOf,
} from './rules';
import type {Checklist, ContactNoteAction, ContactNoteState} from './state';
import {contactNoteReducer, createInitialState} from './state';

const item = (key: string, complete: boolean): RequiredItem => ({
  key,
  complete,
});

const filled = (text: string) => text.trim().length > 0;

const otherFilled = ({selected, other}: Checklist) =>
  !selected.includes(OTHER) || filled(other);

const checklistComplete = (checklist: Checklist) =>
  checklist.selected.length > 0 && otherFilled(checklist);

const choiceComplete = (key: string | null, other: string) =>
  key !== null && (key !== OTHER || filled(other));

const isReached = (state: ContactNoteState) =>
  state.contactMethod !== NOT_REACHED;

const isNotReached = (state: ContactNoteState) => !isReached(state);

export const isNarrativeRequired = isReached;

const isNotFuture = (date: string) => filled(date) && !isFutureDate(date);

export const contactNoteDefinition: NoteDefinition<
  ContactNoteState,
  ContactNoteAction
> = {
  createInitialState,
  reducer: contactNoteReducer,
  composeNarrative,
  sections: {
    encounter: {
      visible: () => true,
      required: (state) => [
        item('member', state.member !== null),
        item('date_of_service', isNotFuture(state.dateOfService)),
      ],
    },
    contact: {
      visible: () => true,
      required: (state) => [
        item(
          'contact_method',
          choiceComplete(state.contactMethod, state.contactMethodDetail),
        ),
      ],
    },
    notReached: {
      visible: isNotReached,
      required: (state) => [
        item(
          'reason_for_attempt',
          choiceComplete(state.notReachedReason, state.notReachedReasonOther),
        ),
      ],
    },
    service: {
      visible: isReached,
      required: (state) => [
        item(
          'place_of_service',
          state.placeOfService?.confirmedFor === state.dateOfService &&
            (state.placeOfService.code !== OTHER_PLACE_OF_SERVICE ||
              filled(state.placeOfServiceOther)),
        ),
        item('service_activities', checklistComplete(state.activities)),
        item('specific_need', filled(state.specificNeed)),
        item('service_description', filled(state.serviceDescription)),
      ],
    },
    reason: {
      visible: isReached,
      required: (state) => [item('reason', checklistComplete(state.reasons))],
    },
    status: {
      visible: isReached,
      required: (state) => [
        item(
          'change_status',
          state.changeStatus !== null && otherFilled(state.needs),
        ),
      ],
    },
    response: {
      visible: isReached,
      required: (state) => [
        item('member_response', checklistComplete(state.responses)),
      ],
    },
    followUp: {
      visible: isReached,
      required: (state) => [
        item(
          'follow_up',
          checklistComplete(state.followUps) &&
            (!state.followUps.selected.includes(FOLLOW_UP_WITH_CLIENT) ||
              filled(state.followUpAt)),
        ),
      ],
    },
    billing: {
      visible: isReached,
      required: (state) => [
        item('service_code', state.serviceCode !== null),
        item(
          'start_end_time',
          durationMinutes(state.startTime, state.endTime) !== null &&
            !isEndInFuture(state),
        ),
      ],
    },
    narrative: {
      visible: () => true,
      required: (state) => [
        ...(isNarrativeRequired(state)
          ? [item('final_narrative', filled(narrativeOf(state)))]
          : []),
      ],
    },
  },
};
